import { useTranslation } from 'react-i18next';
import { getCategoryName } from '../utils/getCategoryName';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Building2,
  MapPin,
  Phone,
  Layers,
  DoorOpen,
  Home,
  Maximize,
  User,
  AlertCircle,
  Loader2,
  ChevronDown,
  ChevronUp,
  Zap,
  Droplets,
  Flame,
  ArrowUpDown,
  Wind,
  TreePine,
  Car,
  Calendar,
  Wrench,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '';

// Types for building info response
interface BuildingInfoBasic {
  name: string;
  address: string;
  floors: number;
  entrances: number;
  apartments: number;
  totalArea: string | null;
  livingArea: string | null;
}

interface BuildingInfoContacts {
  managementCompany: string | null;
  managementPhone: string | null;
  chairman: string | null;
  chairmanPhone: string | null;
  emergencyService: string | null;
}

interface BuildingInfoResponse {
  basic: BuildingInfoBasic;
  contacts: BuildingInfoContacts;
  serviceSpecific: Record<string, string | null>;
  serviceType: string;
  serviceName: string;
  /** The catalogue row, when the payload carries it: its columns follow an
   *  admin rename, the static labels do not. */
  serviceCategory?: {
    key?: string;
    name?: string;
    nameEt?: string | null;
    nameEn?: string | null;
    nameRu?: string | null;
  } | null;
}


// Field icon mappings
const FIELD_ICONS: Record<string, React.ElementType> = {
  electricity: Zap,
  waterSupply: Droplets,
  sewerage: Droplets,
  heating: Flame,
  elevators: ArrowUpDown,
  ventilation: Wind,
  gas: Flame,
  plotArea: TreePine,
  parking: Car,
  playground: Home,
  landscaping: TreePine,
  yearBuilt: Calendar,
  buildingType: Building2,
  foundation: Layers,
  wallMaterial: Layers,
  roofType: Layers,
  entrances: DoorOpen,
  entranceDoors: DoorOpen,
};

interface BuildingInfoCardProps {
  orderId: string;
  className?: string;
  defaultExpanded?: boolean;
}

export function BuildingInfoCard({ orderId, className = '', defaultExpanded = false }: BuildingInfoCardProps) {
  const { t, i18n } = useTranslation();
  const [buildingInfo, setBuildingInfo] = useState<BuildingInfoResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  useEffect(() => {
    const fetchBuildingInfo = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await axios.get(
          `${API_URL}/api/service-orders/${orderId}/building-info`,
          { withCredentials: true }
        );
        if (response.data.success) {
          setBuildingInfo(response.data.data);
        } else {
          setError(response.data.error || 'Failed to load building info');
        }
      } catch (err: any) {
        const errorMessage = err.response?.data?.error || 'Failed to load building info';
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBuildingInfo();
  }, [orderId]);

  if (isLoading) {
    return (
      <div className={`bg-green-50 dark:bg-green-900/20 rounded-lg p-4 ${className}`}>
        <div className="flex items-center justify-center gap-2 text-green-600 dark:text-green-400">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm">{t('buildingInfo.loading')}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-red-50 dark:bg-red-900/20 rounded-lg p-4 ${className}`}>
        <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
          <AlertCircle className="w-4 h-4" />
          <span className="text-sm">{error}</span>
        </div>
      </div>
    );
  }

  if (!buildingInfo) {
    return null;
  }

  const { basic, contacts, serviceSpecific, serviceName, serviceType, serviceCategory } =
    buildingInfo;
  const hasServiceSpecificInfo = Object.keys(serviceSpecific).length > 0;

  return (
    <div className={`bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800 overflow-hidden ${className}`}>
      {/* Header - Always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-green-100/50 dark:hover:bg-green-900/30 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Building2 className="w-5 h-5 text-green-600 dark:text-green-400" />
          <span className="font-medium text-green-800 dark:text-green-300">
            {t('buildingInfo.title')}
          </span>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-green-600 dark:text-green-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-green-600 dark:text-green-400" />
        )}
      </button>

      {/* Expandable content */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-4">
          {/* Basic Info */}
          <div className="space-y-2">
            <h4 className="text-xs font-semibold text-green-700 dark:text-green-400 uppercase tracking-wider">
              {t('buildingInfo.buildingDetails')}
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <InfoItem icon={Building2} label={t('buildingInfo.basic.name')} value={basic.name} />
              <InfoItem icon={MapPin} label={t('buildingInfo.basic.address')} value={basic.address} />
              <InfoItem icon={Layers} label={t('buildingInfo.basic.floors')} value={basic.floors?.toString()} />
              <InfoItem icon={DoorOpen} label={t('buildingInfo.basic.entrances')} value={basic.entrances?.toString()} />
              <InfoItem icon={Home} label={t('buildingInfo.basic.apartments')} value={basic.apartments?.toString()} />
              <InfoItem 
                icon={Maximize} 
                label={t('buildingInfo.basic.totalArea')} 
                value={basic.totalArea ? `${basic.totalArea} m²` : null} 
              />
            </div>
          </div>

          {/* Service-Specific Info */}
          {hasServiceSpecificInfo && (
            <div className="space-y-2">
              <h4 className="text-xs font-semibold text-green-700 dark:text-green-400 uppercase tracking-wider">
                {t('buildingInfo.serviceDetails', {
                  service: getCategoryName(
                    serviceCategory ?? { key: serviceType, name: serviceName },
                    i18n.language,
                    t
                  ),
                })}
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {Object.entries(serviceSpecific).map(([field, value]) => {
                  const Icon = FIELD_ICONS[field] || Wrench;
                  const label = t(`buildingInfo.fields.${field}`, { defaultValue: field });
                  const displayValue = field === 'plotArea' && value ? `${value} m²` : value;
                  return (
                    <InfoItem 
                      key={field} 
                      icon={Icon} 
                      label={label} 
                      value={displayValue} 
                    />
                  );
                })}
              </div>
            </div>
          )}

          {/* Contacts */}
          <div className="space-y-2">
            <h4 className="text-xs font-semibold text-green-700 dark:text-green-400 uppercase tracking-wider">
              {t('buildingInfo.contacts')}
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {contacts.managementCompany && (
                <ContactItem 
                  title={t('buildingInfo.management')} 
                  name={contacts.managementCompany} 
                  phone={contacts.managementPhone} 
                />
              )}
              {contacts.chairman && (
                <ContactItem 
                  title={t('buildingInfo.chairman')} 
                  name={contacts.chairman} 
                  phone={contacts.chairmanPhone} 
                />
              )}
              {contacts.emergencyService && (
                <ContactItem 
                  title={t('buildingInfo.emergency')} 
                  name="24/7" 
                  phone={contacts.emergencyService}
                  emergency 
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Collapsed preview */}
      {!isExpanded && (
        <div className="px-4 pb-3">
          <div className="flex items-center gap-4 text-sm text-green-700 dark:text-green-400">
            <div className="flex items-center gap-1">
              <MapPin className="w-3.5 h-3.5" />
              <span>{basic.address}</span>
            </div>
            {contacts.managementPhone && (
              <div className="flex items-center gap-1">
                <Phone className="w-3.5 h-3.5" />
                <a 
                  href={`tel:${contacts.managementPhone}`}
                  className="hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  {contacts.managementPhone}
                </a>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Helper components
interface InfoItemProps {
  icon: React.ElementType;
  label: string;
  value: string | null | undefined;
}

function InfoItem({ icon: Icon, label, value }: InfoItemProps) {
  if (!value) return null;
  
  return (
    <div className="flex items-center gap-2 text-sm">
      <Icon className="w-4 h-4 text-green-600 dark:text-green-500 flex-shrink-0" />
      <span className="text-muted-foreground">{label}:</span>
      <span className="font-medium text-foreground">{value}</span>
    </div>
  );
}

interface ContactItemProps {
  title: string;
  name: string;
  phone: string | null;
  emergency?: boolean;
}

function ContactItem({ title, name, phone, emergency }: ContactItemProps) {
  return (
    <div className="flex items-start gap-2 p-2">
      <User className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
        emergency 
          ? 'text-red-600 dark:text-red-400' 
          : 'text-muted-foreground'
      }`} />
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{title}</p>
        <p className="font-medium text-sm text-foreground truncate">{name}</p>
        {phone && (
          <a 
            href={`tel:${phone}`}
            className={`text-sm hover:underline ${
              emergency 
                ? 'text-red-600 dark:text-red-400 font-semibold' 
                : 'text-primary'
            }`}
          >
            {phone}
          </a>
        )}
      </div>
    </div>
  );
}

export default BuildingInfoCard;
